import * as React from 'react';
import { useFormContext, Controller } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { Card, CardHeader, CardTitle, CardContent } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Switch } from '@/components/ui/switch';
import { Coins } from 'lucide-react';
import { ImageUpload } from '@/components/form/image-upload';
import { useStore } from '@/lib/api/stores';

interface VariantInformationFormProps {
  storeId?: string;
}

/**
 * Variant information form component
 * Handles variant details using React Hook Form with Controller
 */
export const VariantInformationForm = ({ storeId }: VariantInformationFormProps) => {
  const { data: store } = useStore(storeId || '', !!storeId);
  const isCreditEnabled = store?.creditSettings?.enabled ?? false;
  const { t } = useTranslation(['products', 'common']);
  const {
    control,
    formState: { errors },
  } = useFormContext();

  return (
    <Card>
      <CardHeader>
        <CardTitle>{t('variantInformation')}</CardTitle>
      </CardHeader>
      <CardContent className="space-y-6">
        {/* Basic Information Section */}
        <div>
          <h3 className="mb-4 text-sm font-semibold">
            {t('basicInfo')}
          </h3>
          <div className="space-y-4">
            {/* Image + Name/SKU Row */}
            <div className="flex gap-6">
              <Controller
                name="imageUrl"
                control={control}
                render={({ field }) => (
                  <VariantImageUpload
                    id="variant-image"
                    name="imageUrl"
                    url={field.value || ''}
                    onImageChange={(url) => field.onChange(url || '')}
                  />
                )}
              />

              <div className="flex-1 space-y-4">
                <Controller
                  name="name"
                  control={control}
                  render={({ field }) => (
                    <div className="space-y-2">
                      <Label htmlFor="variant-name">{t('name')} *</Label>
                      <Input
                        id="variant-name"
                        {...field}
                        value={field.value || ''}
                        placeholder={t('variantNamePlaceholder')}
                      />
                      {errors.name && (
                        <p className="mt-1 text-xs leading-tight text-red-600">
                          {errors.name.message as string}
                        </p>
                      )}
                    </div>
                  )}
                />

                <Controller
                  name="sku"
                  control={control}
                  render={({ field }) => (
                    <div className="space-y-2">
                      <Label htmlFor="variant-sku">{t('sku')}</Label>
                      <Input
                        id="variant-sku"
                        {...field}
                        value={field.value || ''}
                        placeholder={t('skuPlaceholder')}
                      />
                      {errors.sku && (
                        <p className="mt-1 text-xs leading-tight text-red-600">
                          {errors.sku.message as string}
                        </p>
                      )}
                    </div>
                  )}
                />
              </div>
            </div>

            {/* Description below */}
            <Controller
              name="description"
              control={control}
              render={({ field }) => (
                <div className="space-y-2">
                  <Label htmlFor="variant-description">
                    {t('description')}
                  </Label>
                  <Textarea
                    id="variant-description"
                    rows={3}
                    {...field}
                    value={field.value || ''}
                    placeholder={t('descriptionPlaceholder')}
                  />
                  {errors.description && (
                    <p className="mt-1 text-xs leading-tight text-red-600">
                      {errors.description.message as string}
                    </p>
                  )}
                </div>
              )}
            />
          </div>
        </div>

        {/* Physical Properties Section */}
        <div className="border-t pt-6">
          <h3 className="mb-4 text-sm font-semibold">
            {t('physicalProperties')}
          </h3>
          <div className="space-y-4">
            <Controller
              name="grams"
              control={control}
              render={({ field }) => (
                <div className="space-y-2">
                  <Label htmlFor="variant-grams">{t('weight')}</Label>
                  <Input
                    id="variant-grams"
                    type="number"
                    value={field.value || ''}
                    onChange={(e) => field.onChange(parseInt(e.target.value) || null)}
                    placeholder={t('weightPlaceholder')}
                  />
                  {errors.grams && (
                    <p className="mt-1 text-xs leading-tight text-red-600">
                      {errors.grams.message as string}
                    </p>
                  )}
                </div>
              )}
            />
          </div>
        </div>

        {/* Credit Price Section — only when credit system is enabled */}
        {isCreditEnabled && (
          <div className="border-t pt-6">
            <h3 className="mb-4 flex items-center gap-2 text-sm font-semibold">
              <Coins className="h-4 w-4 text-purple-600" />
              {t('creditPrice')}
            </h3>
            <Controller
              name="creditPrice"
              control={control}
              render={({ field }) => (
                <div className="space-y-2">
                  <Label htmlFor="variant-credit-price">
                    {t('creditPriceLabel')}
                  </Label>
                  <Input
                    id="variant-credit-price"
                    type="number"
                    min="0"
                    step="1"
                    value={field.value ?? ''}
                    onChange={(e) => field.onChange(e.target.value ? parseInt(e.target.value) : null)}
                    placeholder={t('creditPricePlaceholder')}
                  />
                  <p className="text-muted-foreground text-xs">
                    {t('creditPriceHelp')}
                  </p>
                </div>
              )}
            />
          </div>
        )}

        {/* Status Section */}
        <div className="border-t pt-6">
          <h3 className="mb-4 text-sm font-semibold">{t('status')}</h3>
          <Controller
            name="active"
            control={control}
            render={({ field }) => (
              <div className="flex items-center space-x-2">
                <Switch id="variant-active" checked={field.value} onCheckedChange={field.onChange} />
                <Label htmlFor="variant-active" className="cursor-pointer">
                  {t('active')}
                </Label>
              </div>
            )}
          />
        </div>
        <div className="border-t pt-6">
          <h3 className="mb-4 text-sm font-semibold">{t('inStock')}</h3>
          <Controller
            name="inStock"
            control={control}
            render={({ field }) => (
              <div className="flex items-center space-x-2">
                <Switch id="variant-active" checked={field.value} onCheckedChange={field.onChange} />
                <Label htmlFor="variant-active" className="cursor-pointer">
                  {t('inStock')}
                </Label>
              </div>
            )}
          />
        </div>
      </CardContent>
    </Card>
  );
};

/**
 * Larger image upload component specifically for variant images
 */
const VariantImageUpload = ({
  id,
  name,
  url: initialUrl,
  onImageChange,
}: {
  id: string;
  name: string;
  url: string | undefined | null;
  onImageChange: (url: string | undefined) => void;
}) => {
  const [url, setUrl] = React.useState<string | null>(initialUrl ?? null);
  const { t } = useTranslation(['products', 'common']);

  const handleRemove = async () => {
    if (url) {
      setUrl(null);
      onImageChange(undefined);
      try {
        const { deleteFile } = await import('@/lib/images');
        await deleteFile(url);
      } catch {
        // Ignore error
      }
    }
  };

  const handleImageAvailable = (newUrl: string) => {
    setUrl(newUrl);
    onImageChange(newUrl);
  };

  return (
    <div className="space-y-3">
      {url ? (
        <div className="relative w-56">
          <div className="aspect-square overflow-hidden rounded-lg border-2">
            <img src={url} alt="Variant" className="bg-muted/30 h-full w-full object-cover" />
          </div>
          <button
            type="button"
            onClick={handleRemove}
            className="bg-background hover:bg-destructive hover:text-destructive-foreground absolute -right-1 -top-1 rounded-full p-1 shadow-md transition-all"
            aria-label={t('common:formComponents.removeImage')}
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              width="12"
              height="12"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <line x1="18" y1="6" x2="6" y2="18" />
              <line x1="6" y1="6" x2="18" y2="18" />
            </svg>
          </button>
        </div>
      ) : (
        <div className="bg-muted/30 flex aspect-square w-56 items-center justify-center rounded-lg border-2 border-dashed">
          <svg
            xmlns="http://www.w3.org/2000/svg"
            width="48"
            height="48"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="1"
            strokeLinecap="round"
            strokeLinejoin="round"
            className="text-muted-foreground"
          >
            <rect x="3" y="3" width="18" height="18" rx="2" ry="2" />
            <circle cx="8.5" cy="8.5" r="1.5" />
            <polyline points="21 15 16 10 5 21" />
          </svg>
        </div>
      )}

      <ImageUpload
        id={id}
        name={name}
        url={url}
        onImageAvailable={handleImageAvailable}
        className="h-9 w-56"
      />
    </div>
  );
};
